import { useEffect, useRef, type ReactNode } from 'react'

/**
 * `data-*` PASSTHROUGH, AND WHY IT IS NOT OPTIONAL POLISH.
 *
 * This component used to accept exactly four props and silently drop everything
 * else. TypeScript did not object, because JSX does not type-check attribute
 * names containing a hyphen — so `<Reveal data-region="presenters">` compiled,
 * read correctly to every human who has looked at it, and rendered no attribute
 * at all.
 *
 * That attribute is load-bearing on the bespoke CMS sites: `data-region` is what
 * the editable-field declarations are checked against, so dropping it turns
 * three correctly-annotated fields into `region-mismatch` — the canvas points at
 * the wrong DOM node, and the only symptom is a warning count in the corner of
 * the editor. Found 2026-08-19 on Sam Sparrow's /engagements, where the
 * attribute had been written and discarded ever since.
 *
 * Passing the whole `data-*` family rather than `data-region` alone: any
 * annotation a site spreads onto this component has the same problem, and an
 * allowlist of one would just move the trap.
 */
type RevealProps = {
  children: ReactNode
  className?: string
  delay?: number
  as?: 'div' | 'section' | 'li'
} & { [dataAttribute: `data-${string}`]: string | undefined }

/**
 * Scroll-triggered fade-up. Pairs with `.reveal` / `.is-visible`, which now
 * live in ./effects.css — sites import it via `@gjl/site-kit/effects.css` and
 * tune `--fx-reveal-distance` / `--fx-reveal-duration` / `--fx-ease`.
 *
 * This component was byte-for-byte identical across the bespoke sites. Its CSS
 * was NOT: the line that used to sit here — "the animation itself stays
 * per-design" — licensed 30 copies in 6 variants, and the variance turned out
 * to be three drifting numbers, not design. Hence the variables.
 */
export function Reveal({ children, className = '', delay = 0, as = 'div', ...dataAttributes }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          io.disconnect()
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Narrow the polymorphic tag to one intrinsic element so the ref types cleanly
  // (runtime still renders `as`). A literal-type cast, permitted by the cast-ban.
  const Tag = as as 'div'
  return (
    <Tag
      ref={ref}
      className={`reveal ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...dataAttributes}
    >
      {children}
    </Tag>
  )
}
